import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import ChevronIcon from "../../assets/img/chevron_up.svg?react";
import NavTooltipWrapper from "./NavTooltipWrapper";

// Sub-menu content component
const SubMenuContent = ({ subMenuItems, location, onItemClick }) => (
  <div className="sidenav__submenu">
    {subMenuItems.map((subItem) => {
      const isActive = location.pathname === subItem.to;
      return (
        <NavLink
          key={subItem.key}
          to={subItem.to}
          className={({ isActive }) =>
            ["sidenav__submenu__item", isActive ? "is-active" : ""]
              .filter(Boolean)
              .join(" ")
          }
          onClick={onItemClick}
        >
          {isActive && (
            <span
              className="sidenav__submenu__bullet"
              aria-hidden="true"
              data-testid="submenu-bullet"
            />
          )}
          <span className="sidenav__submenu__label">{subItem.label}</span>
        </NavLink>
      );
    })}
  </div>
);

SubMenuContent.propTypes = {
  subMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    })
  ).isRequired,
  location: PropTypes.object.isRequired,
  onItemClick: PropTypes.func.isRequired,
};

const NavItemWithSubMenu = ({
  label,
  Icon = null,
  collapsed = false,
  subMenuItems,
  children = null,
  onItemClick = null,
  dataNavKey = null,
  // Optional unique identifier for tooltip targeting - allows NavMenuTooltip to target this specific submenu item,
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const buttonRef = useRef();
  const location = useLocation();

  // Check if any sub-menu item is active
  // Use exact match or path prefix match (with trailing slash check)
  const isAnySubMenuActive = subMenuItems.some((item) => {
    const currentPath = location.pathname;
    const itemPath = item.to;

    // Exact match
    if (currentPath === itemPath) return true;

    // Prefix match: current path should start with item path followed by /
    // This prevents false positives (e.g., /home shouldn't match /property-config/information)
    if (currentPath.startsWith(itemPath + "/")) return true;

    return false;
  });

  // Handle parent click - toggle submenu
  const handleParentClick = (e) => {
    if (collapsed) {
      // In collapsed state, toggle popover
      e.preventDefault();
      setIsPopoverOpen(!isPopoverOpen);
    } else {
      // In expanded state, toggle inline submenu
      e.preventDefault();
      setIsSubMenuOpen(!isSubMenuOpen);
    }
    // Call onItemClick callback if provided
    if (onItemClick) {
      onItemClick();
    }
  };

  // Handle submenu item click in popover
  const handleSubmenuItemClick = () => {
    if (collapsed) {
      setIsPopoverOpen(false);
    }
    // Call onItemClick callback if provided
    if (onItemClick) {
      onItemClick();
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isPopoverOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        // Check if click is outside both button and popover
        const popoverElement = document.querySelector(
          ".sidenav__submenu-popover"
        );
        if (!popoverElement?.contains(event.target)) {
          setIsPopoverOpen(false);
        }
      }
    };

    if (isPopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isPopoverOpen]);

  // Auto-close submenu when navigating away from all submenu items.
  // Only react to route changes — not to isSubMenuOpen toggling.
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    if (pathnameChanged && !collapsed && !isAnySubMenuActive && isSubMenuOpen) {
      setIsSubMenuOpen(false);
    }
  }, [location.pathname, collapsed, isAnySubMenuActive, isSubMenuOpen]);

  // Parent link
  const parentLink = (
    <button
      ref={buttonRef}
      type="button"
      className={[
        "sidenav__link",
        "sidenav__link--parent",
        isAnySubMenuActive ? "is-active" : "",
        collapsed ? "is-collapsed" : "",
        isSubMenuOpen && !collapsed ? "is-expanded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleParentClick}
      aria-expanded={collapsed ? isPopoverOpen : isSubMenuOpen}
      aria-haspopup="true"
      // Apply data-nav-key attribute if provided - used by NavMenuTooltip for unique targeting
      {...(dataNavKey && { "data-nav-key": dataNavKey })}
    >
      <span
        className="sidenav__link__icon"
        aria-hidden="true"
        data-testid="nav-icon"
      >
        {Icon ? <Icon /> : null}
      </span>
      {!collapsed && <span className="sidenav__link__label">{label}</span>}
      {!collapsed && (
        <span
          className={["sidenav__link__chevron", isSubMenuOpen ? "is-open" : ""]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
          data-testid="nav-chevron"
        >
          <ChevronIcon />
        </span>
      )}
    </button>
  );

  return (
    <div className="sidenav__item-with-submenu">
      {collapsed ? (
        <>
          <NavTooltipWrapper collapsed={collapsed} label={label}>
            {parentLink}
          </NavTooltipWrapper>

          {/* Manual popover for click - positioned absolutely */}
          {isPopoverOpen && buttonRef.current && (
            <div
              className="sidenav__submenu-popover sidenav__submenu-popover--manual"
              style={{
                position: "fixed",
                left: buttonRef.current.getBoundingClientRect().right + 10,
                top: buttonRef.current.getBoundingClientRect().top,
                zIndex: 10001,
              }}
            >
              <SubMenuContent
                subMenuItems={subMenuItems}
                location={location}
                onItemClick={handleSubmenuItemClick}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {parentLink}
          {isSubMenuOpen && (
            <SubMenuContent
              subMenuItems={subMenuItems}
              location={location}
              onItemClick={handleSubmenuItemClick}
            />
          )}
        </>
      )}
      {children}
    </div>
  );
};

NavItemWithSubMenu.propTypes = {
  label: PropTypes.string.isRequired,
  Icon: PropTypes.elementType,
  collapsed: PropTypes.bool,
  subMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    })
  ).isRequired,
  children: PropTypes.node,
  onItemClick: PropTypes.func,
  dataNavKey: PropTypes.string,
};

export default NavItemWithSubMenu;
